import { useState } from "react";
import { ChevronLeft, Gauge, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { WheelSheet } from "@/components/WheelSheet";
import { useAuth } from "@/context/AuthContext";
import { presetPupitres, pupitreChoices } from "@/lib/pupitres";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const inputCls = "card-bg border-app rounded-xl h-12 px-4 text-base";
const areaCls = "card-bg border-app rounded-xl p-4 text-base min-h-28 resize-y";
const labelCls = "block text-sm font-medium mb-2";
const boxCls = "card-bg border border-app rounded-xl px-5 py-2 text-center hover:border-[var(--brand-accent)] transition-colors";

export const PieceFormSheet = ({ categories, initial, onClose, onSubmit, saving }) => {
  const { t, group } = useAuth();
  const editing = !!initial?.id;
  const instruments = group?.instruments || [];
  const pupChoices = pupitreChoices(group);
  const [name, setName] = useState(initial?.name || "");
  const [composer, setComposer] = useState(initial?.composer || "");
  const [mins, setMins] = useState(initial?.duration_sec ? Math.floor(initial.duration_sec / 60) : 0);
  const [secs, setSecs] = useState(initial?.duration_sec ? initial.duration_sec % 60 : 0);
  const [tempo, setTempo] = useState(initial?.tempo_bpm || 90);
  const [description, setDescription] = useState(initial?.description || "");
  const [history, setHistory] = useState(initial?.history || "");
  const [catId, setCatId] = useState(initial?.parent_id || "");
  const [tags, setTags] = useState(initial?.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [wheel, setWheel] = useState(null); // "duration" | "tempo" | null
  const [versionName, setVersionName] = useState(editing ? "" : `${t("pfs.version_default")} ${new Date().getFullYear()}`);
  const [pupSel, setPupSel] = useState(() => (editing ? [] : presetPupitres(group)));
  const [pupCustom, setPupCustom] = useState("");

  const addCustomPup = () => {
    const v = pupCustom.trim();
    if (v && !pupSel.includes(v)) setPupSel([...pupSel, v]);
    setPupCustom("");
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t) && tags.length < 20) setTags([...tags, t]);
    setTagInput("");
  };

  const submit = () => {
    const duration_sec = mins * 60 + secs;
    onSubmit({
      name: name.trim(),
      parent_id: catId,
      composer: composer.trim(),
      duration_sec: duration_sec > 0 ? duration_sec : null,
      tempo_bpm: clamp(tempo, 20, 300),
      description: description.trim(),
      history: history.trim(),
      tags,
      ...(editing ? {} : { version_name: versionName.trim(), pupitres: pupSel }),
    });
  };

  return (
    <div className="fixed inset-0 z-50 app-bg overflow-y-auto" data-testid="piece-form-sheet">
      <div className="max-w-2xl mx-auto px-5 py-4 pb-10">
        <div className="flex items-center gap-2 mb-6">
          <button onClick={onClose} className="p-2 -ml-2 t-secondary hover:text-white" data-testid="piece-form-back" aria-label={t("common.back")}>
            <ChevronLeft className="h-7 w-7" />
          </button>
          <h1 className="font-heading text-2xl font-extrabold">{t(editing ? "lib.edit_piece" : "lib.new_piece")}</h1>
        </div>

        <div className="space-y-5">
          <div>
            <label className={labelCls} htmlFor="piece-name-input">{t("usr.col_name")} <span className="accent">*</span></label>
            <Input id="piece-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="piece-name-input" autoFocus />
          </div>

          <div>
            <label className={labelCls} htmlFor="piece-composer-input">{t("pfs.composer")}</label>
            <Input id="piece-composer-input" value={composer} onChange={(e) => setComposer(e.target.value)} className={inputCls} data-testid="piece-composer-input" />
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-4">
            <div>
              <p className={labelCls}>{t("pfs.duration")}</p>
              <button type="button" onClick={() => setWheel("duration")} className={boxCls} data-testid="piece-duration-box" aria-label={t("pfs.set_duration")}>
                <span className="font-heading text-3xl font-extrabold" data-testid="piece-duration-value">{mins}:{String(secs).padStart(2, "0")}</span>
                <p className="text-xs t-secondary">min : sec</p>
              </button>
            </div>
            <div>
              <p className={labelCls}>{t("pfs.tempo")}</p>
              <div className="flex items-center gap-2">
                <div className={boxCls}>
                  <input
                    type="number"
                    min={20}
                    max={300}
                    value={tempo}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") { setTempo(""); return; }
                      const v = parseInt(raw, 10);
                      if (!isNaN(v)) setTempo(v);
                    }}
                    onBlur={() => setTempo((v) => clamp(parseInt(v, 10) || 90, 20, 300))}
                    onFocus={(e) => e.target.select()}
                    className="font-heading text-3xl font-extrabold bg-transparent text-center w-20 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:hidden [&::-webkit-inner-spin-button]:hidden"
                    data-testid="piece-tempo-input"
                    aria-label={t("pfs.set_tempo")}
                  />
                  <p className="text-xs t-secondary">bpm</p>
                </div>
                <button type="button" onClick={() => setWheel("tempo")} className="card-bg border-app rounded-xl w-12 h-12 flex items-center justify-center hover:bg-white/5 shrink-0" data-testid="piece-tempo-box" aria-label={t("pfs.set_tempo")}>
                  <Gauge className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className={labelCls} htmlFor="piece-description-input">{t("pfs.description")}</label>
            <Textarea id="piece-description-input" value={description} onChange={(e) => setDescription(e.target.value)} className={areaCls} data-testid="piece-description-input" />
          </div>

          <div>
            <label className={labelCls} htmlFor="piece-history-input">{t("pfs.history")}</label>
            <Textarea id="piece-history-input" value={history} onChange={(e) => setHistory(e.target.value)} className={areaCls} data-testid="piece-history-input" />
          </div>

          <div>
            <p className={labelCls}>{t("pfs.category")}</p>
            <div className="flex flex-wrap gap-2" data-testid="piece-category-chips">
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCatId(c.id)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${catId === c.id ? "accent-bg text-slate-900 font-semibold" : "border border-app t-secondary hover:bg-white/5"}`}
                  data-testid={`piece-category-${c.id}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {!editing && (
            <>
              <div>
                <label className={labelCls} htmlFor="piece-version-input">{t("pfs.initial_version")}</label>
                <Input id="piece-version-input" value={versionName} onChange={(e) => setVersionName(e.target.value)} placeholder={t("lib.version_name_placeholder")} className={inputCls} data-testid="piece-version-input" />
              </div>

              <div>
                <p className={labelCls}>{t("pfs.pupitres_label")}</p>
                {pupChoices.length === 0 ? (
                  <p className="text-xs t-secondary" data-testid="piece-no-instruments">{t("lib.no_instruments_hint")}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2" data-testid="piece-pupitre-checkboxes">
                    {pupChoices.map((i) => (
                      <label key={i} className="flex items-center gap-2.5 text-sm cursor-pointer border border-app rounded-xl px-3 py-3 hover:bg-white/5 transition-colors">
                        <input
                          type="checkbox"
                          className="accent-emerald-500 h-4 w-4"
                          checked={pupSel.includes(i)}
                          onChange={(e) => setPupSel(e.target.checked ? [...pupSel, i] : pupSel.filter((x) => x !== i))}
                          data-testid={`piece-pupitre-check-${i}`}
                        />
                        {i}
                      </label>
                    ))}
                  </div>
                )}
                {pupSel.filter((p) => !pupChoices.includes(p)).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="piece-custom-pupitres">
                    {pupSel.filter((p) => !pupChoices.includes(p)).map((p) => (
                      <span key={p} className="inline-flex items-center gap-1.5 border border-app rounded-full px-3 py-1.5 text-sm">
                        {p}
                        <button type="button" onClick={() => setPupSel(pupSel.filter((x) => x !== p))} className="t-secondary hover:text-rose-400" data-testid={`piece-custom-pupitre-remove-${p}`} aria-label={t("lib.remove_named", { name: p })}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  <Input
                    value={pupCustom}
                    onChange={(e) => setPupCustom(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomPup(); } }}
                    placeholder={t("lib.custom_pupitre_placeholder")}
                    className={inputCls}
                    data-testid="piece-custom-pupitre-input"
                  />
                  <button type="button" onClick={addCustomPup} disabled={!pupCustom.trim()} className="card-bg border-app rounded-xl w-12 h-12 flex items-center justify-center hover:bg-white/5 shrink-0" data-testid="piece-custom-pupitre-add" aria-label={t("common.add")}>
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </>
          )}

          <div>
            <p className={labelCls}>Tags</p>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="piece-tags-list">
                {tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-1.5 border border-app rounded-full px-3 py-1.5 text-sm" data-testid={`piece-tag-chip-${tag}`}>
                    {tag}
                    <button type="button" onClick={() => setTags(tags.filter((x) => x !== tag))} className="t-secondary hover:text-rose-400" data-testid={`piece-tag-remove-${tag}`} aria-label={t("lib.remove_named", { name: tag })}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder={t("pfs.tag_placeholder")}
                className={inputCls}
                data-testid="piece-tag-input"
              />
              <button type="button" onClick={addTag} className="card-bg border-app rounded-xl w-12 h-12 flex items-center justify-center hover:bg-white/5 shrink-0" data-testid="piece-tag-add-button" aria-label={t("pfs.add_tag")}>
                <Plus className="h-5 w-5" />
              </button>
            </div>
          </div>

          <Button
            className="w-full accent-bg text-slate-900 font-bold h-14 text-base rounded-xl"
            onClick={submit}
            disabled={!name.trim() || !catId || saving}
            data-testid="create-piece-confirm"
          >
            {t(editing ? "pfs.save_changes" : "pfs.create_piece")}
          </Button>
        </div>
      </div>

      {wheel === "duration" && (
        <WheelSheet
          title={t("pfs.duration")}
          columns={[
            { key: "min", label: "min", values: Array.from({ length: 100 }, (_, i) => i), value: mins },
            { key: "sec", label: "sec", values: Array.from({ length: 60 }, (_, i) => i), value: secs },
          ]}
          onClose={() => setWheel(null)}
          onConfirm={([m, s]) => { setMins(m); setSecs(s); setWheel(null); }}
        />
      )}
      {wheel === "tempo" && (
        <WheelSheet
          title={t("pfs.tempo")}
          columns={[{ key: "bpm", label: "bpm", values: Array.from({ length: 281 }, (_, i) => i + 20), value: typeof tempo === "number" ? tempo : 90 }]}
          onClose={() => setWheel(null)}
          onConfirm={([b]) => { setTempo(b); setWheel(null); }}
        />
      )}
    </div>
  );
};
